import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Users, Shield, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin:      'bg-red-100 text-red-800',
  manager:    'bg-blue-100 text-blue-800',
  accountant: 'bg-green-100 text-green-800',
  caretaker:  'bg-yellow-100 text-yellow-800',
  support:    'bg-purple-100 text-purple-800',
};

const TeamManagement = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [availPermissions, setAvailPermissions] = useState([]);
  const [availRoles, setAvailRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, rRes, pRes] = await Promise.all([
        axios.get(`${API}/team`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/team/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMembers(mRes.data);
      setAvailRoles(rRes.data.roles);
      setAvailPermissions(rRes.data.permissions);
      setProperties(pRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load team', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/team/${deleting.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Team member removed' });
      setDeleting(null);
      fetchData();
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Users /> Team Management</h1>
          <p className="text-gray-600 mt-1">Manage staff, roles & permissions</p>
        </div>
        <Button data-testid="add-team-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus size={18} className="mr-2" /> Add Member
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : members.length === 0 ? (
        <Card className="p-12 text-center" data-testid="team-empty">
          <Users size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No team members yet</h3>
          <p className="text-gray-600 mb-4">Add caretakers, managers, and accountants to your team</p>
          <Button onClick={() => setShowForm(true)} className="bg-blue-600"><Plus size={18} className="mr-2" /> Add First Member</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
          {members.map(m => (
            <Card key={m.id} className="p-5 hover:shadow-lg transition-shadow" data-testid={`team-card-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {m.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-500">{m.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`team-edit-${m.id}`} onClick={() => { setEditing(m); setShowForm(true); }} className="p-1 text-gray-500 hover:text-blue-600"><Edit2 size={16} /></button>
                  <button data-testid={`team-delete-${m.id}`} onClick={() => setDeleting(m)} className="p-1 text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${ROLE_COLORS[m.role] || 'bg-gray-100 text-gray-800'}`}>
                  <Shield size={10} className="inline mr-1" /> {m.role}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${m.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {m.status}
                </span>
              </div>
              {m.email && <p className="text-xs text-gray-600 mb-2">{m.email}</p>}
              <div className="text-xs text-gray-500">
                <p><strong>{m.assigned_property_ids?.length || 0}</strong> properties • <strong>{m.permissions?.length || 0}</strong> permissions</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <TeamFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          properties={properties}
          availRoles={availRoles}
          availPermissions={availPermissions}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Remove Team Member"
        message="Are you sure you want to remove this team member?"
        itemName={deleting?.name || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

const TeamFormModal = ({ open, onClose, onSuccess, editing, properties, availRoles, availPermissions, token }) => {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', role: 'caretaker',
    assigned_property_ids: [], permissions: [], status: 'active',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || '', phone: editing.phone || '', email: editing.email || '',
        role: editing.role || 'caretaker',
        assigned_property_ids: editing.assigned_property_ids || [],
        permissions: editing.permissions || [],
        status: editing.status || 'active',
      });
    }
  }, [editing]);

  const toggleInArray = (key, val) => {
    setForm(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/team/${editing.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Team member updated' });
      } else {
        await axios.post(`${API}/team`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Team member added' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="team-form">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="team-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
            <div><Label>Phone *</Label><Input data-testid="team-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required /></div>
            <div className="col-span-2"><Label>Email</Label><Input data-testid="team-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div>
              <Label>Role *</Label>
              <select data-testid="team-role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                {availRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select data-testid="team-status" value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Assigned Properties</Label>
            <div className="border border-gray-200 rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
              {properties.length === 0 && <p className="text-xs text-gray-500">No properties available</p>}
              {properties.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    data-testid={`team-prop-${p.id}`}
                    type="checkbox"
                    checked={form.assigned_property_ids?.includes(p.id)}
                    onChange={() => toggleInArray('assigned_property_ids', p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Permissions</Label>
            <div className="border border-gray-200 rounded-lg p-3 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {availPermissions.map(perm => (
                <label key={perm} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    data-testid={`team-perm-${perm}`}
                    type="checkbox"
                    checked={form.permissions?.includes(perm)}
                    onChange={() => toggleInArray('permissions', perm)}
                  />
                  <span>{perm}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button data-testid="team-submit" type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Add Member')}</Button>
            <Button data-testid="team-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TeamManagement;
